import { localDate, PLATFORM_TIMEZONE } from "@cafe/shared";
import { Button, Card, Field, Input } from "@cafe/ui";
import { useState } from "react";

export type Period = { from: string; to: string };
const shift = (d: string, days: number) => new Date(Date.parse(d) + days * 86_400_000).toISOString().slice(0, 10);

/** Период по умолчанию — с 1-го числа текущего месяца по сегодня (пояс платформы). */
export function usePeriod(): [Period, (p: Period) => void] {
  const today = localDate(PLATFORM_TIMEZONE);
  return useState<Period>({ from: `${today.slice(0, 8)}01`, to: today });
}

/** Период отчёта: даты «с — по» и быстрые варианты. */
export function PeriodFilter({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const today = localDate(PLATFORM_TIMEZONE);
  const presets: { label: string; p: Period }[] = [
    { label: "Этот месяц", p: { from: `${today.slice(0, 8)}01`, to: today } },
    { label: "Прошлый месяц", p: (() => { const end = shift(`${today.slice(0, 8)}01`, -1); return { from: `${end.slice(0, 8)}01`, to: end }; })() },
    { label: "30 дней", p: { from: shift(today, -29), to: today } },
    { label: "Год", p: { from: `${today.slice(0, 4)}-01-01`, to: today } },
  ];
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label="С" className="w-40"><Input type="date" value={value.from} max={value.to} onChange={(e) => e.target.value && onChange({ ...value, from: e.target.value })} /></Field>
      <Field label="По" className="w-40"><Input type="date" value={value.to} min={value.from} max={today} onChange={(e) => e.target.value && onChange({ ...value, to: e.target.value })} /></Field>
      <div className="flex flex-wrap gap-2 pb-1">
        {presets.map((x) => (
          <Button key={x.label} size="sm" variant={x.p.from === value.from && x.p.to === value.to ? "primary" : "outline"} onClick={() => onChange(x.p)}>{x.label}</Button>
        ))}
      </div>
    </div>
  );
}

export function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "danger" | "success" }) {
  return (
    <Card className="flex flex-col gap-1 p-4 sm:p-5">
      <span className="text-sm text-muted">{label}</span>
      <span className={`text-2xl font-semibold tabular-nums ${tone === "danger" ? "text-danger" : tone === "success" ? "text-success" : ""}`}>{value}</span>
      {hint && <span className="text-xs text-subtle">{hint}</span>}
    </Card>
  );
}

/** Столбики по дням/месяцам; подпись выбранного столбика — над графиком (наведение и клавиатура). */
export function Bars({ data, format, label }: { data: { key: string; value: number; caption: string }[]; format: (v: number) => string; label: string }) {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  const cur = active === null ? null : data[active];
  return (
    <div className="flex flex-col gap-2">
      <div className="h-6 text-sm" aria-live="polite">
        {cur && <span><span className="text-muted">{cur.caption} · </span><span className="font-semibold tabular-nums">{format(cur.value)}</span></span>}
      </div>
      <div className="flex h-48 items-end gap-0.5 border-b border-border" role="list" aria-label={label}>
        {data.map((d, i) => (
          <div key={d.key} role="listitem" tabIndex={0} aria-label={`${d.caption}: ${format(d.value)}`}
            onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(i)} onBlur={() => setActive(null)}
            className="flex h-full min-w-1 flex-1 items-end outline-none">
            <div className={`w-full rounded-t-[4px] bg-primary transition-opacity ${active === null || active === i ? "" : "opacity-40"}`}
              style={{ height: `${Math.max(d.value > 0 ? 2 : 0, (d.value / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted tabular-nums">
        <span>{data[0]?.caption}</span>
        <span>{data.length > 1 && data[data.length - 1]!.caption}</span>
      </div>
    </div>
  );
}

/** Горизонтальная полоса доли (структура продаж, способы оплаты). */
export function ShareRow({ label, value, total, format }: { label: string; value: number; total: number; format: (v: number) => string }) {
  const p = total > 0 ? (value / total) * 100 : 0;
  return (
    <li className="flex flex-col gap-1">
      <div className="flex justify-between gap-3 text-sm"><span>{label}</span><span className="shrink-0 font-semibold tabular-nums">{format(value)} <span className="font-normal text-muted">· {Math.round(p)}%</span></span></div>
      <div className="h-2 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-primary" style={{ width: `${p}%` }} /></div>
    </li>
  );
}
